import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { BaseConnectionFormController } from '../../../../../../../../modules/connections/connection-form.controller';
import { AudioBridgeProvider, ConferenceModes, IConnectionDetailsDTO } from '../../../../../../../../api/connections.dao';
import { AudioBridgeConnectionField as FieldKey, TelephoneNumberProvider, customAudioProviderOption, configList } from './audio-bridge-connection.form-config';
import { FormVmItemConfigType } from '../../../../../../../../infrastructure/form-viewmodel-controller';
import { IZoomDAO, IZoomTelephoneNumberSetDTO } from '../../../../../../../../api/zoom.dao';
import { IErrorParsing } from '../../../../../../../../infrastructure/error-parsing';
import { alertServerError } from '../../../../../../../global/modal/alerts-host/alert-templates';
import { COMMON_ITEMS_PER_PAGE } from '../../../../../../../../constants/pagination.contants';
import { AudioBridgeProviderLabel } from '../../../../../../../../infrastructure/utils/common.utils';

export class AudioBridgeConnectionFormVm extends BaseConnectionFormController {
    @IoC.Inject private readonly zoomDao: IZoomDAO;
    @mobx.observable
    private telephoneNumberSets: { value: string; label: string; provider: string }[] = [];
    @mobx.observable
    public isLoading: boolean = false;


    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    public static get configList(): FormVmItemConfigType[] { return configList; }

    public static create(connection: IConnectionDetailsDTO): AudioBridgeConnectionFormVm {
        return new AudioBridgeConnectionFormVm(connection);
    }

    private constructor(connection: IConnectionDetailsDTO) {
        super(connection, AudioBridgeConnectionFormVm.configList);
        this.init(connection);
    }

    public getProviderOptions(): { value: AudioBridgeProvider; label: AudioBridgeProvider }[] {
        return [AudioBridgeProvider.ZOOM, AudioBridgeProvider.TURBOBRIDGE, AudioBridgeProvider.OTHER]
            .map(val => ({ value: AudioBridgeProviderLabel[val] as AudioBridgeProvider, label: AudioBridgeProviderLabel[val] as AudioBridgeProvider }));
    }

    public toApiDTO(): IConnectionDetailsDTO {
        const data = super.toApiDTO();
        const audioProviderName = 'audioProviderName';
        if (this.meetingConnectionId && data.audioProviderId !== customAudioProviderOption.value) {
            data.participantTelephoneNumbers = '';
        }
        if (data.audioProviderId === customAudioProviderOption.value) {
            data.audioProviderId = null;
        }

        delete data[audioProviderName];
        return data;
    };

    private async init(connection: IConnectionDetailsDTO): Promise<void> {
        await this.fetchTelephoneNumberSets();
        this.setCustomAudioProviderId(connection.audioProviderId, connection.participantTelephoneNumbers);
        if (this.meetingConnectionId) {
            this.setAudioProviderId(connection.audioProviderId);
        }
        if (connection.audioProviderId) {
            const providerData = this.getAudioProviderLabel(connection.audioProviderId);

            this.setValue(FieldKey.PROVIDER, TelephoneNumberProvider[providerData.provider]);
            this.setValue(FieldKey.AUDIO_PROVIDER_NAME, providerData.name);
        }
    }

    public getConferenceModeOptions(): { value: ConferenceModes; label: ConferenceModes }[] {
        return [ConferenceModes.Presentation, ConferenceModes.QA, ConferenceModes.Conversation, ConferenceModes.Hosts]
            .map(val => ({ value: val, label: val }));
    }

    public getTelephoneNumberSetOptions(): { value: string; label: string }[] {
        const options = [customAudioProviderOption, ...this.telephoneNumberSets];
        return options;
    }

    public parseTelephoneNumberSetOptions(telephoneNumberSets: IZoomTelephoneNumberSetDTO[]): { value: string; label: string; provider: string }[] {

        return telephoneNumberSets
            .map(val => {
                const option = `${AudioBridgeProviderLabel[val.provider]}: ${val.name}`;
                return { value: val.id, label: option, provider: val.provider };
            })
            .sort((prev, next) => prev.label.localeCompare(next.label));
    }

    @mobx.action
    public setCustomAudioProviderId(audioProviderId: string | null, participantTelephoneNumbers: string | null): void {
        if (!audioProviderId) {
            if (this.meetingConnectionId) {
                this.setValue(FieldKey.AUDIO_PROVIDER_ID, customAudioProviderOption.value);
            }
            /* Check cloned connection */
            if (!this.meetingConnectionId && participantTelephoneNumbers?.length) {
                this.setValue(FieldKey.AUDIO_PROVIDER_ID, customAudioProviderOption.value);
            }
        }
    }

    public getAudioProviderLabel(value: string | null): { provider: string; name: string } {
        if (!value || value === customAudioProviderOption.value) {
            return { provider: '', name: '' };
        }

        const provider = this.telephoneNumberSets.find(item => item.value.toString() === value.toString());
        const providerSplitLabel = provider.label?.split(':');
        return { provider: provider.provider, name: providerSplitLabel[1].trim() };
    }

    @mobx.action
    public setAudioProviderId(audioProviderId: string | null): void {
        if (audioProviderId) {
            const telephoneNumberSet = this.telephoneNumberSets.find(value => value.value === audioProviderId);
            if (!telephoneNumberSet?.value) {
                this.setValue(FieldKey.AUDIO_PROVIDER_ID, customAudioProviderOption.value);
            }
            const providerData = this.getAudioProviderLabel(audioProviderId);
            this.setValue(FieldKey.PROVIDER, TelephoneNumberProvider[providerData.provider]);

            this.setValue(FieldKey.AUDIO_PROVIDER_NAME, providerData.name);
        }
    }

    @mobx.action
    public selectTelephoneNumberSet(value: string | null): void {
        const providerData = this.getAudioProviderLabel(value);

        this.setValue(FieldKey.AUDIO_PROVIDER_ID, value);
        this.setValue(FieldKey.PROVIDER, TelephoneNumberProvider[providerData.provider]);
        this.setValue(FieldKey.AUDIO_PROVIDER_NAME, providerData.name);
    }

    @mobx.action
    public async fetchTelephoneNumberSets(): Promise<void> {
        try {
            mobx.runInAction(() => {
                this.isLoading = true;
            });
            const telephoneNumberSetsResponse =
                await this.zoomDao.getTelephoneNumberSets({
                    page: 0,
                    size: COMMON_ITEMS_PER_PAGE,
                    query: '',
                    sortField: null,
                    sortOrder: null
                });
            mobx.runInAction(() => {
                this.telephoneNumberSets = this.parseTelephoneNumberSetOptions(telephoneNumberSetsResponse.content);
            });
        } catch (error) {
            AudioBridgeConnectionFormVm.handleApiException(error);
        } finally {
            mobx.runInAction(() => {
                this.isLoading = false;
            });
        }
    };


}
